import React from 'react';
import { FailureReason } from '../types';
import { AlertCircle, Brain, EyeOff, Calculator, FileQuestion, X } from 'lucide-react';

interface FailureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectReason: (reason: FailureReason) => void;
}

export const FailureModal: React.FC<FailureModalProps> = ({
  isOpen,
  onClose,
  onSelectReason,
}) => {
  if (!isOpen) return null;

  const failureOptions: {
    id: FailureReason;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    {
      id: 'OBSCURED_THEORY',
      title: 'Falha de Decodificação',
      description: 'Não consegui identificar qual era o assunto central ou teoria exigida pelo enunciado.',
      icon: EyeOff,
    },
    {
      id: 'FORGOT_FORMULA',
      title: 'Falha de Memória',
      description: 'Identifiquei o assunto correto, mas esqueci a fórmula exata ou como deduzi-la.',
      icon: Brain,
    },
    {
      id: 'CALCULATION_ERROR',
      title: 'Falha de Execução / Travamento',
      description: 'Identifiquei a teoria e a fórmula certa, mas travei na manipulação algébrica ou conta.',
      icon: Calculator,
    },
    {
      id: 'MISREAD_INPUTS',
      title: 'Leitura Incorreta de Dados',
      description: 'Errei ou deixei passar grandezas dadas no texto ou unidades de medida.',
      icon: FileQuestion,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-lg border border-paper-300 bg-paper-100 p-6 shadow-xl dark:border-study-line dark:bg-study-panel">
        <div className="flex items-center justify-between border-b border-paper-200 pb-3 dark:border-study-line">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              Registrar Causa-Raiz do Travamento
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-3 text-xs text-zinc-600 dark:text-zinc-400">
          O vestibular é decidido na identificação imediata de padrões. Onde exatamente ocorreu o atrito?
        </p>

        <div className="mt-4 space-y-2">
          {failureOptions.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  onSelectReason(opt.id);
                  onClose();
                }}
                className="flex w-full items-start gap-3 rounded border border-paper-300 bg-paper-50 p-3 text-left transition hover:border-zinc-500 hover:bg-paper-200/50 dark:border-study-line dark:bg-study-card dark:hover:border-zinc-500 dark:hover:bg-study-line/50"
              >
                <Icon className="h-4 w-4 text-zinc-600 dark:text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">{opt.title}</p>
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{opt.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
